import type { Pivot } from '@empathyco/playboard-core-domain'
import type { ComputedRef, Ref } from 'vue'
import type { Filter, PivotableToolConfig, RegularFilter } from '../models'
import { computed, getCurrentInstance, ref } from 'vue'
import { FilterTypeEnum } from '../enums'
import { TOOLING_CONSTS } from '../store/tooling/tooling.consts'
import { FiltersUtils } from '../utils/filters.utils'
import { useFeature } from './use-feature'
import { useFilters } from './use-filters'
import { mapGetters } from './utils'

export interface ToolingPivotsComposable {
  pivots: ComputedRef<Array<RegularFilter>>
  visiblePivots: ComputedRef<Array<RegularFilter>>
  setPivotSelectedValues: (
    pivot: RegularFilter,
    selectedValues: RegularFilter['selectedValues'],
  ) => void
  clearPivotsSelectedValues: (options?: ClearPivotsSelectedValuesOptions) => void
  getPivotDefaultValues: (
    pivot: RegularFilter,
    forceSelection?: boolean,
  ) => RegularFilter['selectedValues']
}

export interface ClearPivotsSelectedValuesOptions {
  setDefaultValues?: boolean
  forceSelection?: boolean
}

export interface ToolingPivotsComposableParams {
  pivotable?: Ref<Array<Pivot>>
  extraConfig?: {
    default: PivotExtraConfig
    [pivotId: string]: Partial<PivotExtraConfig>
  }
}

export interface PivotExtraConfig {
  mandatory: boolean
  multiselect: boolean
}

export function useToolingPivots({
  pivotable = ref([]),
  extraConfig = {
    default: {
      mandatory: false,
      multiselect: true,
    },
  },
}: ToolingPivotsComposableParams = {}): ToolingPivotsComposable {
  const ctx = getCurrentInstance()
  if (!ctx) {
    throw new Error("'ctx' is not defined")
  }
  const {
    proxy: { $store },
  } = ctx

  const { featureId } = useFeature()
  const { availableFilters, isFilterVisible } = useFilters()

  const { state: toolingState } = mapGetters($store, TOOLING_CONSTS.moduleName, {
    state: TOOLING_CONSTS.getters.state,
  })

  const toolConfig = computed<PivotableToolConfig>(
    () => toolingState.value[featureId.value]?.config ?? { pivots: [] },
  )

  const pivots = computed<Array<RegularFilter>>(() =>
    Object.values(availableFilters.value).reduce<Array<RegularFilter>>((result, filter) => {
      if (isPivot(filter)) {
        const filterConfig = filter.config as RegularFilter['config']
        result.push({
          config: getPivotConfig(filterConfig),
          selectedValues: getPivotSelectedValues(filterConfig),
        })
      }
      return result
    }, []),
  )

  const visiblePivots = computed<Array<RegularFilter>>(() =>
    pivots.value.filter(pivot => isFilterVisible(pivot.config)),
  )

  function setPivotSelectedValues(
    pivot: RegularFilter,
    selectedValues: RegularFilter['selectedValues'],
  ): void {
    const pivotEncodedId = FiltersUtils.getEncodedId(pivot.config)
    const pivotIndex = pivotable.value.findIndex(({ name }) => name === pivotEncodedId)
    const encodedSelectedValues = selectedValues.map(value =>
      FiltersUtils.encodeValue(pivot.config, value),
    )
    const newPivot: Pivot = {
      name: pivotEncodedId,
      values: encodedSelectedValues,
    }
    if (pivotIndex < 0) {
      pivotable.value.push(newPivot)
    } else {
      pivotable.value.splice(pivotIndex, 1, newPivot)
    }
  }

  function clearPivotsSelectedValues({
    setDefaultValues = false,
    forceSelection = false,
  }: ClearPivotsSelectedValuesOptions = {}): void {
    pivots.value.forEach(pivot => {
      const selectedValues =
        setDefaultValues || !isFilterVisible(pivot.config)
          ? getPivotDefaultValues(pivot, forceSelection)
          : []
      setPivotSelectedValues(pivot, selectedValues)
    })
  }

  function getPivotDefaultValues(
    pivot: RegularFilter,
    forceSelection = false,
  ): RegularFilter['selectedValues'] {
    return FiltersUtils.getDefaultSelectedValues({ ...pivot, selectedValues: [] }, forceSelection)
  }

  function isPivot(filter: Filter): boolean {
    if (filter.config.type !== FilterTypeEnum.REGULAR) {
      return false
    }
    const filterConfig = filter.config as RegularFilter['config']
    const filterEncodedId = FiltersUtils.getEncodedId(filterConfig)
    return toolConfig.value.pivots.some(pivot => pivot.name === filterEncodedId)
  }

  function getPivotConfig(filterConfig: RegularFilter['config']): RegularFilter['config'] {
    const filterExtraConfig = extraConfig[filterConfig.id]
    const mandatory = filterExtraConfig?.mandatory ?? extraConfig.default.mandatory
    const multiselect = filterExtraConfig?.multiselect ?? extraConfig.default.multiselect
    return {
      ...filterConfig,
      mandatory,
      multiselectDisabled: !multiselect,
    }
  }

  function getPivotSelectedValues(
    filterConfig: RegularFilter['config'],
  ): RegularFilter['selectedValues'] {
    const filterEncodedId = FiltersUtils.getEncodedId(filterConfig)
    const pivot = pivotable.value.find(({ name }) => name === filterEncodedId)
    return pivot?.values.map(value => FiltersUtils.decodeValue(filterConfig, value)) ?? []
  }

  return {
    pivots,
    visiblePivots,
    setPivotSelectedValues,
    clearPivotsSelectedValues,
    getPivotDefaultValues,
  }
}
